const HTTPS_PATTERN = /^https:\/\//i;
const IDENTIFIER_PATTERN = /^[A-Za-z0-9._-]+$/;

export interface UserDataLocator {
  dataPath: string[];
  key: string;
}

export interface UserStateRecord {
  dataPath?: unknown;
  key?: unknown;
  valueType?: unknown;
  value?: unknown;
  updatedAt?: unknown;
}

export interface UserMetricRecord {
  dataPath?: unknown;
  key?: unknown;
  value?: unknown;
  updatedAt?: unknown;
}

export type UserStateValueType = "string" | "file" | "number" | "boolean";

export type UserStateValueConversion =
  | { ok: true; value: string | number | boolean }
  | {
    ok: false;
    reason: "missing-record" | "unsupported-type" | "invalid-conversion";
  };

export type UserStateRedirectContext =
  | { ready: false; reason: "editor" | "site" | "user" | "locator" }
  | {
    ready: true;
    siteId: string;
    userId: string;
    dataPath: string[];
    key: string;
  };

export const isUserDataIdentifier = (value: unknown): value is string =>
  typeof value === "string"
  && value.trim().length > 0
  && !value.includes("/")
  && !value.includes("\\")
  && value.trim() !== "."
  && value.trim() !== ".."
  && IDENTIFIER_PATTERN.test(value.trim());

export function normalizeUserDataPath(value: unknown): string[] {
  if (value === undefined || value === null || value === "") return [];
  if (!Array.isArray(value)) {
    throw new Error("User dataPath must be an array of path segments.");
  }

  const dataPath: string[] = [];
  const appendSegment = (segment: unknown) => {
    if (Array.isArray(segment)) {
      segment.forEach(appendSegment);
      return;
    }
    const normalized = typeof segment === "string" ? segment.trim() : "";
    if (!normalized) return;
    if (!isUserDataIdentifier(normalized)) {
      throw new Error(
        "User dataPath segments may contain only letters, numbers, dots, underscores, and hyphens.",
      );
    }
    dataPath.push(normalized);
  };
  value.forEach(appendSegment);
  return dataPath;
}

export function normalizeUserDataLocator(input: {
  dataPath: unknown;
  key: unknown;
}): UserDataLocator {
  const key = typeof input.key === "string" ? input.key.trim() : "";
  if (!isUserDataIdentifier(key)) {
    throw new Error(
      "User data keys may contain only letters, numbers, dots, underscores, and hyphens.",
    );
  }
  return {
    dataPath: normalizeUserDataPath(input.dataPath),
    key,
  };
}

export function resolveUserStateRedirectContext(input: {
  editor: boolean;
  siteId: unknown;
  userId: unknown;
  dataPath: unknown;
  key: unknown;
}): UserStateRedirectContext {
  if (input.editor) return { ready: false, reason: "editor" };

  const siteId = typeof input.siteId === "string" ? input.siteId.trim() : "";
  if (!siteId) return { ready: false, reason: "site" };

  const userId = typeof input.userId === "string" ? input.userId.trim() : "";
  if (!userId) return { ready: false, reason: "user" };

  try {
    const locator = normalizeUserDataLocator({
      dataPath: input.dataPath,
      key: input.key,
    });
    return { ready: true, siteId, userId, ...locator };
  } catch {
    return { ready: false, reason: "locator" };
  }
}

function userDataPath(
  userId: string,
  category: "state" | "metrics",
  dataPath: unknown,
  key: unknown,
): string {
  const locator = normalizeUserDataLocator({ dataPath, key });
  return ["data", userId, category, ...locator.dataPath, locator.key]
    .map((segment) => userDataFileSlug(segment))
    .join("/");
}

export function userDataFileSlug(value: string, maxLength = 128): string {
  let slug = value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .normalize("NFC")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, " ")
    .trim();

  if (slug.length > maxLength) {
    slug = slug.substring(0, maxLength).trim();
  }

  slug = slug.replace(/\s+/g, "-").replace(/-+/g, "-");
  if (!slug) {
    throw new Error("User data path segments must produce a valid file slug.");
  }
  return slug;
}

export const userStatePath = (
  userId: string,
  dataPath: unknown,
  key: unknown,
) => userDataPath(userId, "state", dataPath, key);

export const userMetricPath = (
  userId: string,
  dataPath: unknown,
  key: unknown,
) => userDataPath(userId, "metrics", dataPath, key);

export function getUserStateValue(
  record: UserStateRecord | null | undefined,
): unknown {
  if (record?.valueType === "string" || record?.valueType === "file") {
    return typeof record.value === "string" ? record.value : undefined;
  }
  if (record?.valueType === "number") {
    return typeof record.value === "number" && Number.isFinite(record.value)
      ? record.value
      : undefined;
  }
  if (record?.valueType === "boolean") {
    return typeof record.value === "boolean" ? record.value : undefined;
  }
  return undefined;
}

export function convertUserStateValue(
  record: UserStateRecord | null | undefined,
  expectedType: UserStateValueType,
): UserStateValueConversion {
  if (!record) return { ok: false, reason: "missing-record" };

  const actualType = record.valueType;
  const value = getUserStateValue(record);
  if (value === undefined) {
    return { ok: false, reason: "unsupported-type" };
  }

  if (expectedType === "string") {
    if (
      actualType === "string"
      || actualType === "file"
      || actualType === "number"
      || actualType === "boolean"
    ) {
      return { ok: true, value: String(value) };
    }
  }

  if (expectedType === "file") {
    return actualType === "file" || actualType === "string"
      ? { ok: true, value: String(value) }
      : { ok: false, reason: "invalid-conversion" };
  }

  if (expectedType === "number") {
    if (actualType === "number" && typeof value === "number") {
      return Number.isFinite(value)
        ? { ok: true, value }
        : { ok: false, reason: "invalid-conversion" };
    }
    if (
      (actualType === "string" || actualType === "file")
      && typeof value === "string"
      && value.trim() !== ""
    ) {
      const converted = Number(value);
      return Number.isFinite(converted)
        ? { ok: true, value: converted }
        : { ok: false, reason: "invalid-conversion" };
    }
  }

  if (expectedType === "boolean") {
    if (actualType === "boolean" && typeof value === "boolean") {
      return { ok: true, value };
    }
    if (
      (actualType === "string" || actualType === "file")
      && typeof value === "string"
    ) {
      const normalized = value.trim().toLowerCase();
      if (normalized === "true") return { ok: true, value: true };
      if (normalized === "false") return { ok: true, value: false };
    }
    if (actualType === "number" && (value === 0 || value === 1)) {
      return { ok: true, value: value === 1 };
    }
  }

  return { ok: false, reason: "invalid-conversion" };
}

export function getUserMetricValue(
  record: UserMetricRecord | null | undefined,
): number | undefined {
  const value = Number(record?.value);
  return Number.isFinite(value) ? value : undefined;
}

export function isAllowedUserStateTarget(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const target = value.trim();
  if (!target) return false;

  return (
    (target.startsWith("/") && !target.startsWith("//")) ||
    HTTPS_PATTERN.test(target)
  );
}

export function selectUserStateTarget(
  record: UserStateRecord | null | undefined,
  fallback: unknown,
): string | null {
  if (
    (record?.valueType === "string" || record?.valueType === "file") &&
    isAllowedUserStateTarget(record.value)
  ) {
    return record.value.trim();
  }

  const configuredFallback =
    typeof fallback === "string" && fallback.trim()
      ? fallback.trim()
      : "/";
  return isAllowedUserStateTarget(configuredFallback)
    ? configuredFallback
    : null;
}

export const isCurrentUserStateTarget = (
  href: string,
  currentPath: string,
  external: boolean,
) => !external && href === currentPath;
